import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, Database, Settings as SettingsIcon,
  ChevronLeft, Search, Sun, Moon, LogOut, Bell, ChevronsUpDown, Check, Command as CmdIcon,
  Users as UsersIcon, Menu, Inbox, Wallet, HardHat, ScrollText, Layers,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { BrandMark } from './Brand';
import { CommandPalette } from './CommandPalette';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { roleLabel } from '../lib/roles';
import { cn } from '../lib/utils';
import api from '../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Sheet, SheetContent } from './ui/sheet';

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, route: '/', module: 'dashboard' },
  { label: 'Tenders', icon: FolderKanban, route: '/projects', module: 'tenders' },
  { label: 'Costing', icon: Wallet, route: '/costing', module: 'costing' },
  { label: 'Projects', icon: HardHat, route: '/budget', module: 'project' },
  { label: 'Approvals', icon: Inbox, route: '/approvals', module: null, badge: 'approvals' },
  { label: 'Rate Masters', icon: Database, route: '/masters', module: 'rate_masters' },
  { label: 'Activity Master', icon: Layers, route: '/activities', module: 'rate_masters' },
  { label: 'User & Access', icon: UsersIcon, route: '/users', module: 'users' },
  { label: 'Settings', icon: SettingsIcon, route: '/settings', module: 'settings' },
  { label: 'Audit Trail', icon: ScrollText, route: '/audit-trail', module: 'settings', adminOnly: true },
];

export function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const { user, logout, can } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const loadProjects = useCallback(async () => {
    try { const { data } = await api.get('/projects'); setProjects(data); } catch (e) { console.warn('Failed to load projects', e); }
  }, []);
  useEffect(() => { loadProjects(); }, [loadProjects, location.pathname]);

  // C12 — Approvals badge: count of items awaiting this user. Refresh on route
  // change, on a light interval, and whenever an approval is actioned anywhere.
  const [approvalsCount, setApprovalsCount] = useState(0);
  const loadApprovalsCount = useCallback(async () => {
    try { const { data } = await api.get('/approvals/count'); setApprovalsCount(data.count || 0); }
    catch (e) { console.warn('Failed to load approvals count', e); }
  }, []);
  useEffect(() => { loadApprovalsCount(); }, [loadApprovalsCount, location.pathname]);
  useEffect(() => {
    const onChange = () => loadApprovalsCount();
    window.addEventListener('approvals:changed', onChange);
    const id = setInterval(loadApprovalsCount, 45000);
    return () => { window.removeEventListener('approvals:changed', onChange); clearInterval(id); };
  }, [loadApprovalsCount]);

  // Close the mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isActive = (route) => (route === '/' ? location.pathname === '/' : location.pathname.startsWith(route));
  const activeProject = projects.find((p) => location.pathname.includes(p.id));
  const isAdmin = user?.groupName === 'Admin';
  const navItems = NAV.filter((item) => (!item.module || can(item.module, 'read')) && (!item.adminOnly || isAdmin));

  const NavButton = ({ item, mobile = false }) => {
    const active = isActive(item.route);
    const showLabel = mobile || !collapsed;
    const count = item.badge === 'approvals' ? approvalsCount : 0;
    return (
      <button
        data-testid={`sidebar-nav-item-${item.route === '/' ? 'dashboard' : item.route.slice(1)}`}
        onClick={() => { navigate(item.route); setMobileOpen(false); }}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
          active ? 'bg-[hsl(var(--primary)/0.12)] text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          !mobile && collapsed && 'justify-center px-0',
        )}
      >
        {active && <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-r-full bg-[hsl(var(--copper))]" style={{ width: 3 }} />}
        <span className="relative">
          <item.icon size={18} className={active ? 'text-[hsl(var(--teal))]' : ''} />
          {count > 0 && (!showLabel) && (
            <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-[hsl(var(--copper))] px-1 text-[9px] font-bold leading-none text-white"
              data-testid="approvals-badge-collapsed">{count > 9 ? '9+' : count}</span>
          )}
        </span>
        {showLabel && <span>{item.label}</span>}
        {showLabel && count > 0 && (
          <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-[hsl(var(--copper))] px-1.5 text-[10px] font-bold leading-none text-white"
            data-testid="approvals-badge">{count > 99 ? '99+' : count}</span>
        )}
      </button>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-screen bg-background">
        {/* Desktop sidebar */}
        <motion.aside
          data-testid="app-sidebar"
          animate={{ width: collapsed ? 76 : 256 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="sticky top-0 z-30 hidden h-screen flex-col border-r bg-card md:flex"
        >
          <div className={cn('flex h-16 items-center overflow-hidden border-b px-4', collapsed && 'justify-center px-0')}>
            {collapsed ? <BrandMark size="sm" showText={false} /> : <BrandMark size="md" />}
          </div>
          <nav className="flex-1 space-y-1 p-3">
            {navItems.map((item) => (collapsed ? (
              <Tooltip key={item.route}>
                <TooltipTrigger asChild><div><NavButton item={item} /></div></TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : <NavButton key={item.route} item={item} />))}
          </nav>
          <div className="p-3">
            <button
              data-testid="sidebar-collapse-button"
              onClick={() => setCollapsed((c) => !c)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs text-muted-foreground hover:bg-muted"
            >
              <ChevronLeft size={16} className={cn('transition-transform', collapsed && 'rotate-180')} />
              {!collapsed && 'Collapse'}
            </button>
          </div>
        </motion.aside>

        {/* Mobile drawer sidebar */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-0" data-testid="mobile-nav-drawer">
            <div className="flex h-16 items-center border-b px-4"><BrandMark size="md" /></div>
            <nav className="space-y-1 p-3">
              {navItems.map((item) => <NavButton key={item.route} item={item} mobile />)}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Topbar */}
          <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b bg-card/80 px-3 backdrop-blur sm:gap-3 md:px-6">
            <button
              data-testid="mobile-nav-trigger"
              onClick={() => setMobileOpen(true)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border hover:bg-muted md:hidden"
              aria-label="Open navigation"
            >
              <Menu size={18} />
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="topbar-project-switcher" className="flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-sm hover:bg-muted sm:px-3">
                  <FolderKanban size={15} className="text-[hsl(var(--teal))]" />
                  <span className="hidden max-w-[180px] truncate font-medium sm:inline">{activeProject ? activeProject.code : 'Select tender'}</span>
                  <ChevronsUpDown size={14} className="text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-1.5">
                <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tenders</div>
                <div className="max-h-72 overflow-y-auto">
                  {projects.map((p) => (
                    <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                      className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-muted">
                      <span className="min-w-0"><span className="font-num text-xs text-[hsl(var(--copper))]">{p.code}</span>
                        <span className="block truncate text-xs text-muted-foreground">{p.name}</span></span>
                      {activeProject?.id === p.id && <Check size={14} className="text-[hsl(var(--teal))]" />}
                    </button>
                  ))}
                  {projects.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">No tenders yet.</div>}
                </div>
              </PopoverContent>
            </Popover>

            {activeProject && (
              <span data-testid="topbar-revision-badge" className="hidden items-center gap-1.5 rounded-full border bg-muted px-2.5 py-1 text-xs font-medium sm:inline-flex">
                Rev {activeProject.currentRevNo || 0}
              </span>
            )}

            <button
              data-testid="command-palette-trigger"
              onClick={() => setPaletteOpen(true)}
              className="ml-auto flex items-center gap-2 rounded-xl border bg-background px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted sm:px-3"
            >
              <Search size={15} />
              <span className="hidden md:inline">Search…</span>
              <kbd className="hidden items-center gap-0.5 rounded border bg-card px-1.5 py-0.5 font-num text-[10px] md:inline-flex"><CmdIcon size={10} />K</kbd>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-notifications-menu" className="relative hidden h-9 w-9 place-items-center rounded-xl border hover:bg-muted sm:grid">
                  <Bell size={16} />
                  <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--copper))]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-3 text-xs text-muted-foreground">You’re all caught up. Activity appears on the dashboard feed.</div>
              </DropdownMenuContent>
            </DropdownMenu>

            <button data-testid="topbar-theme-toggle" onClick={toggle} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border hover:bg-muted">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-user-menu" className="flex items-center gap-2 rounded-xl border px-2 py-1 hover:bg-muted">
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--primary))] text-xs font-semibold text-[hsl(var(--primary-foreground))]">
                    {(user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('')}
                  </span>
                  <span className="hidden text-left sm:block"><span className="block text-xs font-medium leading-tight">{user?.name}</span>
                    <span className="block text-[10px] leading-tight text-muted-foreground">{user?.groupName || roleLabel(user?.role)}</span></span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>Settings</DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-button" onClick={() => { logout(); navigate('/login'); }} className="text-[hsl(var(--danger))]">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <main className="flex-1">{children}</main>
        </div>

        <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} projects={projects} />
      </div>
    </TooltipProvider>
  );
}
